import type { MockExamItem, TeacherExamAttemptResultItem, TeacherExamItem, WorksheetProgressRow } from '../services/api';
import type { SchoolDashboardData, TestPrepItem } from '../types';
import type { AttemptsByExam } from '../utils/examAnalytics';

/**
 * Browser cache for the leadership dashboard. A first visit makes one request per
 * teacher, exam and section; within CACHE_TTL_MS a reload or a fresh sign-in reuses
 * the result instead. The backend caches for 30 minutes too, so this is no staler.
 */
const PREFIX = 'sx-insights:v1:';
export const CACHE_TTL_MS = 30 * 60 * 1000;

export interface InsightsSnapshot {
  at: number;
  school: SchoolDashboardData;
  exams: TeacherExamItem[];
  attempts: AttemptsByExam;
  worksheetRows: Record<string, WorksheetProgressRow[]>;
  practice?: TestPrepItem[];
  mocks?: MockExamItem[];
}

// Large fields the leadership dashboard never reads (question papers, model answers,
// per-student AI feedback). Dropping them keeps memory and the cache small.
const EXAM_DROP = ['processing_summary', 'question_paper_snapshot', 'answer_sheets_snapshot'];
const ATTEMPT_DROP = ['strengths', 'areas_for_improvement', 'worksheet_recommendations', 'detailed_analysis', 'parent_note', 'remediation_plan'];

const omit = <T extends object>(o: T, keys: string[]): T => {
  const out = { ...o } as Record<string, unknown>;
  keys.forEach((k) => { delete out[k]; });
  return out as T;
};

export const slimExam = (e: TeacherExamItem) => omit(e, EXAM_DROP);
export const slimAttempts = (items: TeacherExamAttemptResultItem[]) => items.map((a) => omit(a, ATTEMPT_DROP));

const keyFor = (schoolCode: string) => PREFIX + schoolCode.toLowerCase();

export function readInsights(schoolCode: string): InsightsSnapshot | null {
  try {
    const raw = window.localStorage.getItem(keyFor(schoolCode));
    if (!raw) return null;
    const snap = JSON.parse(raw) as InsightsSnapshot;
    return snap && Date.now() - snap.at < CACHE_TTL_MS ? snap : null;
  } catch {
    return null;
  }
}

/** Merges `patch` into the stored snapshot; `school` is required when none exists yet. */
export function writeInsights(schoolCode: string, patch: Partial<InsightsSnapshot>) {
  try {
    const cur = readInsights(schoolCode);
    const next = { ...cur, ...patch, at: patch.at ?? cur?.at ?? Date.now() } as InsightsSnapshot;
    if (!next.school) return;
    window.localStorage.setItem(keyFor(schoolCode), JSON.stringify(next));
  } catch {
    // Storage full or blocked: the dashboard still works, it just reloads next time.
  }
}

export function clearInsights(schoolCode?: string) {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX) && (!schoolCode || k === keyFor(schoolCode))) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // Nothing cached, or storage blocked.
  }
}
